import React, { useState } from 'react'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import {
  useApproveLicensingMutation,
  useSubmitPlatformCodeMutation,
} from '@/redux/collaborationsApi'
import { Eyebrow, LinksForm, Pill, fieldCls, linkCls, primaryCls } from './ui'

const asUrl = (l: any) => (typeof l === 'string' ? l : l?.url) as string
const host = (u: string) => {
  try {
    return new URL(u).hostname.replace(/^www\./, '')
  } catch {
    return u
  }
}

/** The live post: paste the links once it's up. Remember #ad. */
export const LiveLinksStep: React.FC<{
  postLinks: any[]
  done: boolean
  busy: boolean
  onSubmit: (links: string[]) => Promise<unknown>
}> = ({ postLinks, done, busy, onSubmit }) => {
  const links = (postLinks ?? []).map(asUrl).filter(Boolean)
  if (done || links.length) {
    return (
      <div className="mt-3 space-y-2">
        <Pill tone="green">Live</Pill>
        <ul className="space-y-1.5">
          {links.map((l) => (
            <li key={l}>
              <a
                href={l}
                target="_blank"
                rel="noreferrer"
                className="inline-flex max-w-full items-center gap-1.5 text-sm font-semibold text-[#3F7A0A] underline underline-offset-2"
              >
                <span className="truncate">{host(l)}</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    )
  }
  return (
    <LinksForm
      label="Paste the link to your post. Keep #ad in the caption."
      placeholder="https://www.instagram.com/p/…"
      button="Mark it live"
      busy={busy}
      onSubmit={(ls) => onSubmit(ls)}
    />
  )
}

type PlatformReq = {
  platform: string
  submission_type: 'code' | 'attestation'
  status: 'pending' | 'completed'
  submitted_value?: string | boolean
  submitted_at?: string
}

const PLATFORM: Record<string, { name: string; what: string; help: string }> = {
  tiktok: {
    name: 'TikTok',
    what: 'Spark Ads code',
    help: 'In TikTok: open the post → Ad settings → turn on Ad authorization → Generate code.',
  },
  instagram: {
    name: 'Instagram',
    what: 'Partnership ad code',
    help: 'In Instagram: open the post → ⋯ → Partnership label and ads → Allow brand to boost → copy the code.',
  },
  youtube: {
    name: 'YouTube',
    what: 'Ad permission',
    help: 'Confirm the brand may run this video as an ad for the agreed window.',
  },
}

/**
 * Ad codes: the brand runs the post as an ad for the agreed window, so it needs the Spark
 * (TikTok) or partnership (Instagram) code from each platform, or a yes for YouTube.
 */
export const CodesStep: React.FC<{
  dealId: string
  milestone: any
  brandName: string
  licenseDays?: number | null
}> = ({ dealId, milestone, brandName, licenseDays }) => {
  const meta = milestone.metadata ?? {}
  const platforms: PlatformReq[] = meta.platforms ?? []
  const approved =
    meta.step === 'ad_code_submission' || platforms.some((p) => p.status === 'completed')
  const [approve, { isLoading: approving }] = useApproveLicensingMutation()
  const [submit, { isLoading: sending }] = useSubmitPlatformCodeMutation()
  const [values, setValues] = useState<Record<string, string>>({})
  const [attest, setAttest] = useState<Record<string, boolean>>({})
  const all = platforms.length > 0 && platforms.every((p) => p.status === 'completed')

  const send = async (p: PlatformReq) => {
    const value = p.submission_type === 'attestation' ? true : (values[p.platform] ?? '').trim()
    if (p.submission_type === 'code' && !value) return
    try {
      await submit({
        campaignId: dealId,
        milestoneId: milestone.id,
        platform: p.platform,
        value,
      }).unwrap()
      toast(`${PLATFORM[p.platform]?.name ?? p.platform} code sent`)
    } catch (err: any) {
      toast.error('That didn’t go through', { description: err?.data?.error ?? err?.message })
    }
  }

  if (all) {
    return (
      <div className="mt-3 space-y-2">
        <Pill tone="green">Codes sent</Pill>
        <ul className="space-y-1.5 text-sm">
          {platforms.map((p) => (
            <li
              key={p.platform}
              className="flex items-center justify-between gap-3 rounded-xl bg-muted px-3 py-2"
            >
              <span className="font-semibold text-foreground">
                {PLATFORM[p.platform]?.name ?? p.platform}
              </span>
              <span className="truncate font-mono text-xs text-muted-foreground">
                {typeof p.submitted_value === 'string' ? p.submitted_value : 'Allowed'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-3">
      <p className="text-sm text-muted-foreground">
        {brandName} runs your post as an ad for {licenseDays ?? 30} days, which is part of what
        you're paid. Each platform gives you a code that lets them.
      </p>
      {!approved ? (
        <button
          type="button"
          className={primaryCls}
          disabled={approving}
          onClick={() => approve({ campaignId: dealId })}
        >
          {approving ? 'One moment…' : 'OK, let me send the codes'}
        </button>
      ) : (
        platforms.map((p) => {
          const info = PLATFORM[p.platform] ?? { name: p.platform, what: 'Code', help: '' }
          const done = p.status === 'completed'
          return (
            <div key={p.platform} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-foreground">
                  {info.name} · {info.what}
                </p>
                {done ? (
                  <Pill tone="green">
                    <Check className="h-3 w-3" /> Sent
                  </Pill>
                ) : null}
              </div>
              {done ? (
                <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
                  {typeof p.submitted_value === 'string' ? p.submitted_value : 'Allowed'}
                </p>
              ) : (
                <>
                  <p className="mt-1 text-xs text-muted-foreground">{info.help}</p>
                  {p.submission_type === 'attestation' ? (
                    <label className="mt-2 flex items-start gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={!!attest[p.platform]}
                        onChange={(e) => setAttest({ ...attest, [p.platform]: e.target.checked })}
                        className="mt-0.5 h-4 w-4"
                      />
                      {brandName} may run this video as an ad.
                    </label>
                  ) : (
                    <input
                      value={values[p.platform] ?? ''}
                      onChange={(e) => setValues({ ...values, [p.platform]: e.target.value })}
                      placeholder={`Paste the ${info.what.toLowerCase()}`}
                      className={`${fieldCls} mt-2 font-mono`}
                    />
                  )}
                  <button
                    type="button"
                    className={`mt-2 ${primaryCls}`}
                    disabled={
                      sending ||
                      (p.submission_type === 'attestation'
                        ? !attest[p.platform]
                        : !(values[p.platform] ?? '').trim())
                    }
                    onClick={() => send(p)}
                  >
                    Send {info.name} {p.submission_type === 'attestation' ? 'permission' : 'code'}
                  </button>
                </>
              )}
            </div>
          )
        })
      )}
      {approved && platforms.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No platforms are set on this license yet. Message {brandName} if they're waiting on
          codes.
        </p>
      ) : null}
    </div>
  )
}

/** A small copy button, for codes and links */
export const CopyButton: React.FC<{ text: string }> = ({ text }) => (
  <button
    type="button"
    className={linkCls}
    onClick={() => navigator.clipboard?.writeText(text).then(() => toast('Copied'))}
  >
    <Copy className="mr-1 inline h-3.5 w-3.5" /> Copy
  </button>
)

export const LiveEyebrow = Eyebrow
